/**
 * Help content model (ADR-012). Pages are Markdown with YAML-like front matter (flat keys, scalar or list values;
 * see content/help/README.md). The same parser serves the in-app help panel and the guide generator.
 */

export type Product = 'field' | 'cloud' | 'ops' | 'mobile';

export interface HelpPage {
  readonly id: string;
  readonly title: string;
  readonly products: readonly Product[];
  readonly roles: readonly string[];
  readonly features: readonly string[];
  readonly phase: number;
  readonly order: number;
  /** Markdown body with `{{tenant.*}}` placeholders and `screenshot:<id>` image sources still in place. */
  readonly body: string;
}

export interface Audience {
  readonly product: Product;
  readonly role: string;
  /** Feature flags switched on for the tenant. */
  readonly features: ReadonlySet<string>;
}

export interface TenantText {
  readonly name: string;
  readonly productTitle: string;
}

const FRONT = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/;

function parseValue(raw: string): string | string[] | number {
  const v = raw.trim();
  if (v.startsWith('[') && v.endsWith(']')) {
    const inner = v.slice(1, -1).trim();
    return inner === '' ? [] : inner.split(',').map((s) => s.trim().replace(/^['"]|['"]$/g, ''));
  }
  if (/^-?\d+(\.\d+)?$/.test(v)) return Number(v);
  return v.replace(/^['"]|['"]$/g, '');
}

/** The front matter's keys and values, and where the body starts; throws on missing or malformed front matter. */
function frontMatter(
  markdown: string,
  source: string,
): { fields: Record<string, string | string[] | number>; bodyStart: number } {
  const m = FRONT.exec(markdown);
  if (!m) throw new Error(`${source}: missing front matter`);
  const fields: Record<string, string | string[] | number> = {};
  for (const line of m[1]!.split(/\r?\n/)) {
    if (!line.trim() || line.trim().startsWith('#')) continue;
    const idx = line.indexOf(':');
    if (idx < 0) throw new Error(`${source}: bad front matter line "${line}"`);
    fields[line.slice(0, idx).trim()] = parseValue(line.slice(idx + 1));
  }
  return { fields, bodyStart: m[0].length };
}

/** Parse one help page; throws on missing front matter or required keys. */
export function parseHelpPage(markdown: string, source = '<memory>'): HelpPage {
  const { fields, bodyStart } = frontMatter(markdown, source);
  const str = (k: string): string => {
    const v = fields[k];
    if (typeof v !== 'string' || !v) throw new Error(`${source}: front matter "${k}" is required`);
    return v;
  };
  const list = (k: string): string[] => {
    const v = fields[k];
    if (v === undefined) return [];
    if (!Array.isArray(v)) throw new Error(`${source}: front matter "${k}" must be a list`);
    return v;
  };
  const num = (k: string, dflt: number): number => {
    const v = fields[k];
    return typeof v === 'number' ? v : dflt;
  };
  const products = list('products') as Product[];
  if (products.length === 0) throw new Error(`${source}: "products" must name at least one product`);
  return {
    id: str('id'),
    title: str('title'),
    products,
    roles: list('roles'),
    features: list('features'),
    phase: num('phase', 0),
    order: num('order', 1000),
    body: markdown.slice(bodyStart),
  };
}

/**
 * A training module (content/training/README.md, ADR-012): a help page with `duration_min` and `prerequisites` in
 * its front matter, shown on Teslyn Field's "Quick-start and training" page (decision 859).
 */
export interface TrainingModule extends HelpPage {
  /** About how long the module takes, in minutes; null when the file does not say. */
  readonly durationMin: number | null;
  /** What a trainee needs before starting, in the file's order. */
  readonly prerequisites: readonly string[];
}

/** Parse one training module; throws as `parseHelpPage` does, or when `prerequisites` is not a list. */
export function parseTrainingModule(markdown: string, source = '<memory>'): TrainingModule {
  const page = parseHelpPage(markdown, source);
  const { fields } = frontMatter(markdown, source);
  const d = fields['duration_min'];
  const pre = fields['prerequisites'];
  if (pre !== undefined && !Array.isArray(pre))
    throw new Error(`${source}: front matter "prerequisites" must be a list`);
  return { ...page, durationMin: typeof d === 'number' ? d : null, prerequisites: pre ?? [] };
}

/** Pages this audience may see, in guide order. A page with no roles is visible to every role. */
export function selectPages<P extends HelpPage>(pages: readonly P[], audience: Audience): P[] {
  return pages
    .filter((p) => p.products.includes(audience.product))
    .filter((p) => p.roles.length === 0 || p.roles.includes(audience.role))
    .filter((p) => p.features.every((f) => audience.features.has(f)))
    .sort((a, b) => a.order - b.order || a.title.localeCompare(b.title));
}

/** Replace `{{tenant.name}}` and `{{tenant.productTitle}}` placeholders. */
export function applyTenantText(body: string, tenant: TenantText): string {
  return body
    .replaceAll('{{tenant.name}}', tenant.name)
    .replaceAll('{{tenant.productTitle}}', tenant.productTitle);
}

/** Screenshot placeholders `![alt](screenshot:<id>)` present in a body, for the capture step in CI. */
export function screenshotIds(body: string): string[] {
  return [...body.matchAll(/!\[[^\]]*]\(screenshot:([a-z0-9._-]+)\)/gi)].map((m) => m[1]!);
}

/**
 * Release notes (ADR-014, content/release-notes/README.md): one Markdown file per version with front matter
 * `version`, `channels`, optionally `date` and optionally `status: draft` until the release is finalised. Notes up
 * to 2026.10.8 carry no date (MB, 26 Oct 2026); later ones do. Notes are ordered by version, never by date.
 */
export interface ReleaseNote {
  readonly version: string;
  /** `YYYY-MM-DD`; null when the file has no `date` (nothing is shown for it). */
  readonly date: string | null;
  readonly channels: readonly string[];
  readonly draft: boolean;
  /** Markdown body (front matter removed). */
  readonly body: string;
}

/** Parse one release notes file; throws on missing front matter or version, or a malformed date or status. */
export function parseReleaseNote(markdown: string, source = '<memory>'): ReleaseNote {
  const m = FRONT.exec(markdown);
  if (!m) throw new Error(`${source}: missing front matter`);
  const fields: Record<string, string | string[] | number> = {};
  for (const line of m[1]!.split(/\r?\n/)) {
    if (!line.trim() || line.trim().startsWith('#')) continue;
    const idx = line.indexOf(':');
    if (idx < 0) throw new Error(`${source}: bad front matter line "${line}"`);
    // versions such as 2026.10 must stay text, so read every scalar as a string here
    const raw = line.slice(idx + 1).trim();
    const value = parseValue(raw);
    fields[line.slice(0, idx).trim()] = typeof value === 'number' ? raw : value;
  }
  const version = fields['version'];
  if (typeof version !== 'string' || parseVersion(version) === null) {
    throw new Error(`${source}: front matter "version" must be YYYY.MM.patch`);
  }
  const date = fields['date'] ?? null;
  if (date !== null && (typeof date !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(date))) {
    throw new Error(`${source}: front matter "date" must be YYYY-MM-DD`);
  }
  const channels = fields['channels'];
  const status = fields['status'];
  if (status !== undefined && status !== 'draft' && status !== 'final') {
    throw new Error(`${source}: front matter "status" must be draft or final`);
  }
  return {
    version,
    date,
    channels: Array.isArray(channels) ? channels : [],
    draft: status === 'draft',
    body: markdown.slice(m[0].length),
  };
}

interface ParsedVersion {
  readonly parts: readonly [number, number, number];
  /** Pre-release identifiers, e.g. `beta.2` → ['beta', '2']; empty for a release. */
  readonly pre: readonly string[];
}

/** `2026.10.1`, `2026.11.0-beta.2`; null when the text is not a version. */
export function parseVersion(text: string): ParsedVersion | null {
  const m = /^(\d+)\.(\d+)\.(\d+)(?:-([0-9A-Za-z.-]+))?$/.exec(text.trim());
  if (!m) return null;
  return { parts: [Number(m[1]), Number(m[2]), Number(m[3])], pre: m[4] ? m[4].split('.') : [] };
}

/**
 * Order two versions (semver precedence): negative when `a` is older. A pre-release is older than its release
 * (`2026.11.0-beta.2` < `2026.11.0`). Throws on text that is not a version.
 */
export function compareVersions(a: string, b: string): number {
  const x = parseVersion(a);
  const y = parseVersion(b);
  if (!x || !y) throw new Error(`not a version: ${!x ? a : b}`);
  for (let i = 0; i < 3; i++) {
    const d = x.parts[i]! - y.parts[i]!;
    if (d !== 0) return d;
  }
  if (x.pre.length === 0 || y.pre.length === 0) return y.pre.length - x.pre.length;
  for (let i = 0; i < Math.min(x.pre.length, y.pre.length); i++) {
    const p = x.pre[i]!;
    const q = y.pre[i]!;
    const pn = /^\d+$/.test(p);
    const qn = /^\d+$/.test(q);
    if (pn && qn && Number(p) !== Number(q)) return Number(p) - Number(q);
    if (pn !== qn) return pn ? -1 : 1;
    if (p !== q) return p < q ? -1 : 1;
  }
  return x.pre.length - y.pre.length;
}

/** The release a version belongs to: `2026.11.0-beta.2` → `2026.11.0`. */
export function releaseOf(version: string): string {
  const v = parseVersion(version);
  if (!v) throw new Error(`not a version: ${version}`);
  return v.parts.join('.');
}

/**
 * Notes a user has not seen yet, newest first: every note after `lastSeen` up to the running version's release (a
 * beta build shows the notes of the release it is a candidate for). `lastSeen` null means everything up to the
 * running version.
 */
export function notesSince(
  notes: readonly ReleaseNote[],
  lastSeen: string | null,
  running: string,
): ReleaseNote[] {
  const upTo = releaseOf(running);
  return notes
    .filter((n) => compareVersions(n.version, upTo) <= 0)
    .filter((n) => lastSeen === null || compareVersions(n.version, lastSeen) > 0)
    .sort((a, b) => compareVersions(b.version, a.version));
}
